import clsx from 'clsx';
import { StaticImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';

import Link from 'components/shared/link';
import MainContext from 'context/main';

import androidSvg from './images/android.svg';
import appleSvg from './images/apple.svg';
import platformsSmSvg from './images/platforms-sm.svg';
import platformsSvg from './images/platforms.svg';

const BentoCards = ({
  title,
  description,
  firstCardText,
  secondCardSubtitle,
  secondCardText,
  thirdCardText,
  smallCards,
  wideCardTitle,
  wideCardLinkText,
  wideCardLinkUrl,
}) => {
  const {
    language: { locale },
  } = useContext(MainContext);

  const isLongTitle = title.length > 36;

  return (
    <section className="bento-cards safe-paddings overflow-hidden pb-24 lg:pb-20 md:pb-16 md:mt-6">
      <div className="container">
        <h2
          className={clsx(
            'text-new-black text-[56px] leading-denser font-medium tracking-tight-4 text-center mx-auto xl:text-[44px] lg:text-[36px] md:text-[32px]',
            isLongTitle
              ? 'max-w-5xl xl:max-w-2xl lg:max-w-xl'
              : 'max-w-[604px] xl:max-w-lg lg:max-w-[400px]'
          )}
        >
          {title}
        </h2>
        <p className="mt-5 text-xl leading-snug font-light text-new-gray-1 text-center max-w-[656px] mx-auto xl:mt-4 xl:text-[18px] xl:max-w-[500px] lg:mt-3.5 lg:text-[16px] lg:max-w-[478px] md:mt-3">
          {description}
        </p>

        <div className="grid grid-cols-12 grid-gap mt-16 xl:mt-12 lg:mt-[45px] md:mt-8 md:grid-cols-4">
          <div className="col-span-6 grid grid-cols-2 gap-8 xl:gap-6 lg:gap-[18px] md:col-span-full">
            <div className="relative border border-[#EAEDF6] rounded-2xl overflow-hidden col-span-full bg-new-gray-2 ">
              <div className="aspect-[1.56] md:mt-1 pointer-events-none">
                <StaticImage
                  className="pointer-events-none md:!hidden sm:!block"
                  src="./images/backend.png"
                  alt="Backend in-a-box"
                  width={592}
                  height={455}
                />
                <StaticImage
                  className="pointer-events-none !hidden md:!block sm:!hidden"
                  imgClassName="!h-auto !w-auto !max-w-full"
                  src="./images/backend-md.png"
                  alt="Backend in-a-box"
                  width={700}
                  height={455}
                />
              </div>
              <p
                className="text-new-gray-1 [&>strong]:font-medium [&>strong]:text-new-black text-lg font-light px-7 pb-7 -mt-[68px] leading-snug xl:px-6 xl:pt-2 xl:pb-6 lg:text-[14px] lg:px-5 lg:pt-8 lg:pb-5 md:pt-3 md:pb-9 md:translate-y-4 md:pr-4"
                dangerouslySetInnerHTML={{ __html: firstCardText }}
              />
            </div>

            <div className="relative hidden overflow-hidden rounded-2xl md:block col-span-full">
              <div className="relative border-[#EAEDF6] rounded-2xl h-full">
                <div className="relative z-10 flex items-center flex-col bg-[#FCFCFD] pt-7 pb-9 h-full rounded-2xl">
                  <span className="absolute w-[618px] h-[534px] rounded-full bg-[#C3CBF3] opacity-[0.12] blur-[90px] -top-2/3 -left-1/2 pointer-events-none" />
                  <span className="absolute w-[614px] h-[535px] rounded-full bg-[#A6F2D2] opacity-10 blur-[90px] -top-[50px] -right-1/2 pointer-events-none" />
                  <img
                    className="absolute top-0 left-0 rounded-tl-2xl z-10 pointer-events-none xl:max-w-[146px] xl:top-[-6px] lg:max-w-[110px] lg:-left-2"
                    src={appleSvg}
                    alt=""
                    width={191}
                    height={200}
                    loading="lazy"
                  />
                  <img
                    className="absolute bottom-0 right-0 rounded-br-2xl z-10 pointer-events-none xl:max-w-[146px] xl:-right-1 lg:max-w-[110px]"
                    src={androidSvg}
                    alt=""
                    width={195}
                    height={181}
                    loading="lazy"
                  />
                  <span className="relative z-10 bg-clip-text font-sans2 text-transparent text-[40px] tracking-tight-4 font-light leading-tight bg-[linear-gradient(92deg,#5A73F2_-14.34%,#4D4D4D_55.83%,#57DBA4_104.93%)]">
                    {secondCardSubtitle}
                  </span>
                  <span className="relative z-10 text-new-gray-1 opacity-70 text-[12px] leading-snug -mt-0.5">
                    {secondCardText}
                  </span>
                </div>
              </div>
            </div>
            <div className="relative hidden border border-[#EAEDF6] rounded-2xl overflow-hidden items-center col-span-full bg-[#F8F9FB] md:flex md:flex-col md:py-6 xs:pt-3 xs:pb-4">
              <div className="precise-image w-full aspect-[1.5] pointer-events-none max-w-[89%] xs:max-w-[96%]">
                <img
                  className="xs:hidden"
                  src={platformsSvg}
                  alt=""
                  width={527}
                  height={350}
                  loading="lazy"
                />
                <img
                  className="hidden xs:block"
                  src={platformsSmSvg}
                  alt=""
                  width={312}
                  height={207}
                  loading="lazy"
                />
              </div>
              <p
                className="text-new-gray-1 [&>strong]:font-medium [&>strong]:text-new-black text-[14px] font-light leading-snug md:mt-5 md:px-5 xs:-mt-1"
                dangerouslySetInnerHTML={{ __html: thirdCardText }}
              />
            </div>

            {smallCards.map(({ subTitle, description }, idx) => (
              <div className="relative rounded-2xl overflow-hidden p-px" key={idx}>
                <span
                  className={clsx(
                    'absolute pointer-events-none w-[476px] z-20 h-[476px] top-[-238px] left-[-238px] opacity-10 rounded-full blur-[90px]',
                    idx % 2 ? 'bg-[#C1F6E0]' : 'bg-[#F7D4D4]'
                  )}
                />
                <div
                  className={clsx(
                    'relative h-full before:absolute before:-inset-px before:rounded-2xl rounded-2xl',
                    idx % 2
                      ? 'before:bg-[linear-gradient(to_right,#D6F5E8_0%,#EBF5F1_100%)]'
                      : 'before:bg-[linear-gradient(to_right,#EEDDE7_0%,#EDEDF3_100%)]'
                  )}
                >
                  <div className="bg-new-gray-2 relative z-10 h-full rounded-2xl flex flex-col justify-center items-center pt-5 pb-[42px] xl:pb-8 lg:pt-[20px] lg:pb-[30px]">
                    <span
                      className={clsx(
                        'text-[80px] tracking-tight-4 bg-clip-text font-sans2 text-transparent font-thin xl:text-[64px] lg:text-[48px]',
                        locale === 'ca' && '!text-[64px] xl:!text-[48px] lg:!text-[40px]',
                        idx % 2
                          ? 'bg-[linear-gradient(112deg,#57DBA4_6.01%,#4D4D4D_76.19%)]'
                          : 'bg-[linear-gradient(124deg,#F2545B_9.14%,#4D4D4D_78.61%)]'
                      )}
                    >
                      {subTitle}
                    </span>
                    <span
                      className={clsx(
                        '-mt-1.5 text-new-gray-1 opacity-70 text-base leading-snug xl:text-[15px] lg:text-[12px] md:-mt-2 xs:text-center',
                        locale === 'ca' && 'px-3 text-center'
                      )}
                    >
                      {description}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="col-span-6 flex flex-col gap-8 xl:gap-6 lg:gap-[18px] md:hidden">
            <div className="relative overflow-hidden rounded-2xl shrink-0">
              <div className="relative border border-[#EAEDF6] rounded-2xl">
                <div className="relative z-10 flex items-center flex-col bg-[#FCFCFD] pt-[50px] pb-[57px] rounded-2xl overflow-hidden xl:pt-10 xl:pb-12 lg:pt-8 lg:pb-[34px]">
                  <span className="absolute w-[618px] h-[534px] rounded-full bg-[#C3CBF3] opacity-[0.12] blur-[90px] -top-2/3 -left-1/2 pointer-events-none" />
                  <span className="absolute w-[614px] h-[535px] rounded-full bg-[#A6F2D2] opacity-10 blur-[90px] -top-[50px] -right-1/2 pointer-events-none" />
                  <img
                    className="absolute top-0 left-0 rounded-tl-2xl z-10 pointer-events-none xl:max-w-[146px] xl:top-[-6px] lg:max-w-[110px] lg:-left-2"
                    src={appleSvg}
                    alt=""
                    width={191}
                    height={200}
                    loading="lazy"
                  />
                  <img
                    className="absolute bottom-0 right-0 rounded-br-2xl z-10 pointer-events-none xl:max-w-[146px] xl:-right-1 lg:max-w-[110px]"
                    src={androidSvg}
                    alt=""
                    width={195}
                    height={181}
                    loading="lazy"
                  />
                  <span className="relative z-10 bg-clip-text font-sans2 text-transparent text-[54px] tracking-tight-4 font-light leading-tight bg-[linear-gradient(92deg,#5A73F2_-14.34%,#4D4D4D_55.83%,#57DBA4_104.93%)] xl:text-[44px] lg:text-[36px]">
                    {secondCardSubtitle}
                  </span>
                  <span className="relative z-10 text-new-gray-1 opacity-70 text-base leading-snug mt-0.5 xl:text-[15px] xl:mt-0 lg:text-[12px]">
                    {secondCardText}
                  </span>
                </div>
              </div>
            </div>
            <div className="relative flex flex-col items-center justify-between border border-[#EAEDF6] rounded-2xl overflow-hidden bg-[#F8F9FB] pt-10 pb-7 xl:pt-6 xl:pb-6 lg:pt-7">
              <div className="precise-image max-w-[527px] w-full aspect-[1.5] pointer-events-none xl:max-w-[93%] lg:max-w-[89%]">
                <img
                  className="xs:hidden"
                  src={platformsSvg}
                  alt=""
                  width={527}
                  height={350}
                  loading="lazy"
                />
              </div>
              <p
                className="text-new-gray-1 [&>strong]:font-medium [&>strong]:text-new-black text-[18px] font-light px-7 leading-snug mt-[18px] xl:mt-2.5 xl:pl-6 xl:text-[16px] lg:px-5 lg:text-[14px] lg:mt-11"
                dangerouslySetInnerHTML={{ __html: thirdCardText }}
              />
            </div>
          </div>
          <div className="relative col-span-full pt-[94px] pb-24 overflow-hidden bg-new-gray-2 border border-[#D6DBF5] rounded-2xl xl:pt-[76px] xl:pb-20 lg:py-18 md:pt-9 md:pb-0 md:flex md:flex-col md:h-80">
            <div className="flex md:hidden flex-col items-start pl-12 xl:pl-11 max-w-96 lg:pl-9 lg:max-w-80 md:items-center md:max-w-full md:px-0">
              <h3 className="z-10 text-[32px] tracking-tight-4 font-medium leading-none xl:text-[28px] lg:text-[24px] md:text-[20px]">
                {wideCardTitle}
              </h3>
              <Link
                className="z-10 mt-[18px] xl:text-[16px] lg:mt-3 lg:text-[14px]"
                to={wideCardLinkUrl}
                theme="secondary-2"
                size="md"
                withArrow
              >
                {wideCardLinkText}
              </Link>
            </div>
            <Link
              className="hidden md:flex flex-col items-center max-w-full px-0 grow"
              to={wideCardLinkUrl}
            >
              <h3 className="z-10 text-[32px] tracking-[-0.04em] font-medium leading-none xl:text-[28px] lg:text-[24px] md:text-[20px]">
                {wideCardTitle}
              </h3>
              <span className="z-10 inline-flex items-center group leading-none font-sans2 relative after:absolute after:w-full after:h-1.5 after:inset-x-0 after:-bottom-1.5 text-secondary-2 no-underline font-medium transition-colors duration-200 ease-in-out hover:text-blue-hover mt-[18px] text-[14px]">
                {wideCardLinkText}
              </span>
            </Link>
            <div className="absolute pointer-events-none right-0 inset-y-0 h-full xl:-right-[26.5%] lg:right-0 lg:left-[28%] lg:h-[87%] lg:top-1/2 lg:-translate-y-[48%] md:left-0 md:right-0 md:hidden">
              <StaticImage
                className="w-[929px] xl:w-[73%] lg:w-full h-full"
                src="./images/logos.png"
                alt="Logos"
                width={929}
                height={259}
                loading="lazy"
              />
              <div className="absolute inset-0 bg-[linear-gradient(#F8F9FB_0%,rgba(217,217,217,0)_10%,rgba(217,217,217,0)_90%,#F8F9FB_100%)]" />
              <div className="absolute inset-0 bg-[linear-gradient(270deg,#F8F9FB_0%,rgba(217,217,217,0)_8%)]" />
            </div>
            <div className="absolute left-1/2 -translate-x-1/2 bottom-0 w-[328px] h-[250px] hidden -mt-5 md:block">
              <StaticImage
                className="w-[328px] h-[250px]"
                src="./images/logos-mobile.png"
                alt="Logos"
                width={328}
                height={250}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

BentoCards.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  firstCardText: PropTypes.string.isRequired,
  secondCardSubtitle: PropTypes.string.isRequired,
  secondCardText: PropTypes.string.isRequired,
  thirdCardText: PropTypes.string.isRequired,
  wideCardTitle: PropTypes.string.isRequired,
  wideCardLinkUrl: PropTypes.string.isRequired,
  wideCardLinkText: PropTypes.string.isRequired,
  smallCards: PropTypes.arrayOf(
    PropTypes.shape({
      subTitle: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default BentoCards;
